import { context, propagation, trace } from '@opentelemetry/api';
import { useCallback, useMemo, useState } from 'react';
import {
  asLegacyResponse,
  createCanvasClient,
  handleApiError,
} from '../clients/canvasApi';
import { useAuth } from '../contexts/Auth';

const useApi = () => {
  const [error, setError] = useState(null);
  const { getValidToken, logout } = useAuth();
  const client = useMemo(() => createCanvasClient(), []);

  const apiCall = useCallback(
    async (requestFn, parentSpan = null) => {
      let extraHeaders = {};
      if (parentSpan && typeof parentSpan.recordException === 'function') {
        const ctx = trace.setSpan(context.active(), parentSpan);
        const carrier = {};
        propagation.inject(ctx, carrier);
        extraHeaders = { ...carrier };
      }

      try {
        const token = await getValidToken();
        if (!token) {
          throw new Error('No valid token available');
        }

        const headers = {
          ...extraHeaders,
          Authorization: `Bearer ${token}`,
        };

        const result = await requestFn(client, headers);
        return asLegacyResponse(result);
      } catch (err) {
        if (parentSpan && typeof parentSpan.recordException === 'function') {
          parentSpan.recordException(err);
        }
        setError(err.message);
        handleApiError(err);
        if (err.response && err.response.status === 401) {
          logout();
          throw new Error('Authentication failed. Please log in again.');
        }
        throw err;
      }
    },
    [client, getValidToken, logout]
  );

  const projects = useMemo(
    () => ({
      list: (parentSpan) =>
        apiCall(
          (c, headers) =>
            c.GET('/projects/', {
              params: { query: { skip: 0, limit: 100 } },
              headers,
            }),
          parentSpan
        ),
      create: (data, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.POST('/projects/', {
              body: data,
              headers,
            }),
          parentSpan
        ),
      get: (id, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.GET('/projects/{project_id}', {
              params: { path: { project_id: id } },
              headers,
            }),
          parentSpan
        ),
      update: (id, data, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.PATCH('/projects/{project_id}', {
              params: { path: { project_id: id } },
              body: data,
              headers,
            }),
          parentSpan
        ),
      delete: (id, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.DELETE('/projects/{project_id}', {
              params: { path: { project_id: id } },
              headers,
            }),
          parentSpan
        ),
      listPackages: (parentSpan) =>
        apiCall(
          (c, headers) =>
            c.GET('/packages', {
              params: { query: { skip: 0, limit: 100 } },
              headers,
            }),
          parentSpan
        ),
      createOrUpdatePackage: (data, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.POST('/packages/create-or-update', {
              body: data,
              headers,
            }),
          parentSpan
        ),
      listProjectPackages: (projectId, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.GET('/projects/{project_id}/packages/', {
              params: {
                path: { project_id: projectId },
                query: { skip: 0, limit: 100 },
              },
              headers,
            }),
          parentSpan
        ),
      createPackage: (projectId, data, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.POST('/projects/{project_id}/packages/', {
              params: { path: { project_id: projectId } },
              body: data,
              headers,
            }),
          parentSpan
        ),
      updatePackage: (projectId, packageId, data, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.PATCH('/projects/{project_id}/packages/{package_id}', {
              params: {
                path: { project_id: projectId, package_id: packageId },
              },
              body: data,
              headers,
            }),
          parentSpan
        ),
      deletePackage: (projectId, packageId, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.DELETE('/projects/{project_id}/packages/{package_id}', {
              params: {
                path: { project_id: projectId, package_id: packageId },
              },
              headers,
            }),
          parentSpan
        ),
      deployPackage: (projectId, packageId, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.POST('/projects/{project_id}/packages/{package_id}/deploy', {
              params: {
                path: { project_id: projectId, package_id: packageId },
              },
              headers,
            }),
          parentSpan
        ),
      // Hand-typed in canvas-api-types.ts — ../api/data/openapi-spec.yaml is missing.
      deployUntil: (projectId, packageId, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.POST(
              '/projects/{project_id}/packages/{package_id}/deploy-until',
              {
                params: {
                  path: { project_id: projectId, package_id: packageId },
                },
                headers,
              }
            ),
          parentSpan
        ),
      runUntil: (projectId, packageId, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.POST('/projects/{project_id}/packages/{package_id}/run-until', {
              params: {
                path: { project_id: projectId, package_id: packageId },
              },
              headers,
            }),
          parentSpan
        ),
      runWorkflow: (projectId, packageId, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.POST('/projects/{project_id}/packages/{package_id}/run', {
              params: {
                path: { project_id: projectId, package_id: packageId },
              },
              headers,
            }),
          parentSpan
        ),
      activateTimer: (projectId, packageId, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.POST('/projects/{project_id}/packages/{package_id}/activate', {
              params: {
                path: { project_id: projectId, package_id: packageId },
              },
              headers,
            }),
          parentSpan
        ),
      deactivateTimer: (projectId, packageId, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.POST('/projects/{project_id}/packages/{package_id}/deactivate', {
              params: {
                path: { project_id: projectId, package_id: packageId },
              },
              headers,
            }),
          parentSpan
        ),
      listPackageJobs: (projectId, packageId, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.GET('/projects/{project_id}/packages/{package_id}/jobs', {
              params: {
                path: { project_id: projectId, package_id: packageId },
              },
              headers,
            }),
          parentSpan
        ),
      previewDeployOrder: (projectId, packageId, mode, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.GET(
              '/projects/{project_id}/packages/{package_id}/deploy-order',
              {
                params: {
                  path: { project_id: projectId, package_id: packageId },
                  query: mode ? { mode } : undefined,
                },
                headers,
              }
            ),
          parentSpan
        ),
      destroyPackage: (projectId, packageId, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.DELETE(
              '/projects/{project_id}/packages/{package_id}/destroy',
              {
                params: {
                  path: { project_id: projectId, package_id: packageId },
                },
                headers,
              }
            ),
          parentSpan
        ),
      listConnections: (projectId, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.GET('/projects/{project_id}/connections/', {
              params: { path: { project_id: projectId } },
              headers,
            }),
          parentSpan
        ),
      createConnection: (projectId, data, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.POST('/projects/{project_id}/connections/', {
              params: { path: { project_id: projectId } },
              body: data,
              headers,
            }),
          parentSpan
        ),
      deleteConnection: (
        projectId,
        sourcePackageId,
        targetPackageId,
        parentSpan
      ) =>
        apiCall(
          (c, headers) =>
            c.DELETE(
              '/projects/{project_id}/source/{source_package_id}/target/{target_package_id}',
              {
                params: {
                  path: {
                    project_id: projectId,
                    source_package_id: sourcePackageId,
                    target_package_id: targetPackageId,
                  },
                },
                headers,
              }
            ),
          parentSpan
        ),
    }),
    [apiCall]
  );

  const credentials = useMemo(
    () => ({
      list: (parentSpan) =>
        apiCall(
          (c, headers) =>
            c.GET('/credentials/', {
              params: { query: { skip: 0, limit: 100 } },
              headers,
            }),
          parentSpan
        ),
      create: (data, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.POST('/credentials/', {
              body: data,
              headers,
            }),
          parentSpan
        ),
      get: (id, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.GET('/credentials/{credential_id}', {
              params: { path: { credential_id: id } },
              headers,
            }),
          parentSpan
        ),
      update: (id, data, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.PATCH('/credentials/{credential_id}', {
              params: { path: { credential_id: id } },
              body: data,
              headers,
            }),
          parentSpan
        ),
      delete: (id, parentSpan) =>
        apiCall(
          (c, headers) =>
            c.DELETE('/credentials/{credential_id}', {
              params: { path: { credential_id: id } },
              headers,
            }),
          parentSpan
        ),
    }),
    [apiCall]
  );

  return { projects, credentials, error };
};

export default useApi;
